import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

/**
 * Wire types for the employees / cashiers admin surface. Mirrors
 * `packages/contracts/schemas/domain/cashier.ts`. The admin response
 * never carries the PIN cleartext (handed over once at create / rotate
 * via the `pin` field on those mutation responses only).
 */

export type CashierStatus = 'active' | 'suspended';

export type Cashier = {
  id: string;
  employeeCode: string;
  /** GP cashierloginname encoded in the Yuki cashier's QR badge. */
  loginName: string | null;
  displayName: string;
  status: CashierStatus;
  isAttendant: boolean;
  /** Assigned role (RFL-207) from the Roles section, or null. */
  role: { id: string; name: string } | null;
  gpStores?: { code: string; id: string | null; name: string | null; active: boolean }[];
  gpStoresSynced?: boolean;
  /** VL-350: home store; null when the cashier is not store-restricted. */
  location?: { id: string; code: string; name: string } | null;
  /** VL-350: the store system's cashier group (e.g. OP) of an imported cashier. */
  storeGroup?: string | null;
  /**
   * VL-351: set when the store system owns this cashier. Back Office then
   * refuses every change to it (409 `IMPORTED_READ_ONLY`).
   */
  managedBy?: { kind: 'store-system'; sourceId: string; sourceName: string } | null;
  /** A disabled source permits emergency edits, but not a persistent role override. */
  roleReadOnly?: boolean;
  pinUpdatedAt: string | null;
  pinResetCount: number;
  createdAt: string;
  updatedAt: string;
};

export type CashierListResponse = {
  cashiers: Cashier[];
  page: number;
  pageSize: number;
  total: number;
};

export type CashierProvisioned = {
  cashier: Cashier;
  /** Cleartext PIN — handed over EXACTLY ONCE. Never displayed again. */
  pin: string;
  pinProvisionedAt: string;
};

export type CashierPinRotated = {
  pin: string;
  pinUpdatedAt: string;
};

const LIST_KEY = ['employees', 'cashiers'] as const;
const ONE_KEY = (id: string) => ['employees', 'cashiers', id] as const;

export type CashierListSort = 'code' | 'name' | 'store' | 'role';

export type CashierListQuery = {
  page?: number;
  pageSize?: number;
  status?: CashierStatus;
  search?: string;
  /** VL-350: a location id, or `none` for cashiers not bound to a store. */
  locationId?: string;
  /** VL-350: a role id, or `none` for cashiers without one. */
  roleId?: string;
  sort?: CashierListSort;
};

export function useCashiers(query: CashierListQuery = {}) {
  return useQuery({
    queryKey: [...LIST_KEY, query] as const,
    queryFn: async (): Promise<CashierListResponse> => {
      const params: Record<string, string | number> = {};
      if (query.page !== undefined) params.page = query.page;
      if (query.pageSize !== undefined) params.pageSize = query.pageSize;
      // The list endpoint reads `status`; the old `filter.status` key was
      // silently dropped, so the Active/Suspended filter did nothing (RL-610).
      if (query.status !== undefined) params.status = query.status;
      if (query.search !== undefined && query.search.length > 0) params.search = query.search;
      if (query.locationId !== undefined) params.locationId = query.locationId;
      if (query.roleId !== undefined) params.roleId = query.roleId;
      if (query.sort !== undefined && query.sort !== 'code') params.sort = query.sort;
      const res = await apiClient.get<CashierListResponse>('/employees/cashiers', { params });
      return res.data;
    },
    // Keep the current page on screen while the next one loads, so the table
    // and the pager do not blank out between pages (RL-610).
    placeholderData: (previous) => previous,
  });
}

export function useCashier(id: string | undefined) {
  return useQuery({
    queryKey: id === undefined ? ['employees', 'cashiers', '__noop'] : ONE_KEY(id),
    enabled: id !== undefined,
    queryFn: async (): Promise<Cashier> => {
      const res = await apiClient.get<Cashier>(`/employees/cashiers/${id}`);
      return res.data;
    },
  });
}

export type CreateCashierInput = {
  employeeCode: string;
  loginName?: string | null;
  displayName: string;
  /** Optional — server generates a random 4-digit PIN when omitted. */
  pin?: string;
};

export function useCreateCashier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCashierInput): Promise<CashierProvisioned> => {
      const res = await apiClient.post<CashierProvisioned>('/employees/cashiers', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

export type PatchCashierInput = {
  loginName?: string | null;
  displayName?: string;
  status?: CashierStatus;
  isAttendant?: boolean;
  /** Assign (string), clear (null), or leave unchanged (omit) the role. */
  roleId?: string | null;
};

export function usePatchCashier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; patch: PatchCashierInput }): Promise<Cashier> => {
      const res = await apiClient.patch<Cashier>(`/employees/cashiers/${vars.id}`, vars.patch);
      return res.data;
    },
    onSuccess: (cashier) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(cashier.id) });
    },
  });
}

export function useRotatePin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; pin?: string }): Promise<CashierPinRotated> => {
      const res = await apiClient.post<CashierPinRotated>(
        `/employees/cashiers/${vars.id}/pin`,
        vars.pin === undefined ? {} : { pin: vars.pin },
      );
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(vars.id) });
    },
  });
}

export function useSoftDeleteCashier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/employees/cashiers/${id}`);
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(id) });
    },
  });
}
